import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { ArrowLeft, Save, Loader2, Building2 } from 'lucide-react';

function ExNewBadge({ status }) {
  return status === 'existing'
    ? <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-200 h-4 px-1.5 font-semibold">Existing</Badge>
    : <Badge variant="outline" className="text-[9px] bg-green-50 text-green-700 border-green-200 h-4 px-1.5 font-semibold">New</Badge>;
}

export default function PropertyEditPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [currentAccount] = useState(() => {
    const s = localStorage.getItem('current_account');
    return s ? JSON.parse(s) : null;
  });
  const accountId = currentAccount?.id;

  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!accountId || !propertyId) return;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/bill2rollin/properties/${propertyId}/accounts`, { params: { account_id: accountId } });
        const p = data.property;
        setProp(p);
        setForm({
          name: p.name || '',
          address: p.address || '',
          city: p.city || '',
          state: p.state || '',
          zip: p.zip || '',
          ein: p.ein || '',
          mysql_site_id: p.mysql_site_id || '',
        });
      } catch { toast.error('Failed to load property'); }
      finally { setLoading(false); }
    };
    load();
  }, [accountId, propertyId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/bill2rollin/properties/${propertyId}?account_id=${accountId}`, form);
      toast.success('Property updated');
      navigate(`/ubm/bill2rollin/property/${propertyId}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  if (!accountId) return <MainLayout><div className="p-8 text-center text-sm text-muted-foreground">Select an organization.</div></MainLayout>;

  return (
    <MainLayout wide>
      <div className="p-4 md:p-6 max-w-2xl" data-testid="property-edit-page">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/ubm/bill2rollin/property/${propertyId}`)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Property
          </Button>
          {prop && (
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-violet-600" />
              <h1 className="text-lg font-bold">Edit Property: {prop.name}</h1>
              <ExNewBadge status={prop.mysql_status || 'new'} />
            </div>
          )}
        </div>

        {loading ? (
          <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : (
          <div className="border rounded-lg p-6 bg-card space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-xs font-medium">Property / Entity Name</Label>
                <Input value={form.name || ''} onChange={(e) => setField('name', e.target.value)} className="h-9 text-sm mt-1" data-testid="prop-edit-name" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-medium">Address</Label>
                <Input value={form.address || ''} onChange={(e) => setField('address', e.target.value)} className="h-9 text-sm mt-1" data-testid="prop-edit-address" />
              </div>
              <div>
                <Label className="text-xs font-medium">City</Label>
                <Input value={form.city || ''} onChange={(e) => setField('city', e.target.value)} className="h-9 text-sm mt-1" data-testid="prop-edit-city" />
              </div>
              <div>
                <Label className="text-xs font-medium">State</Label>
                <Input value={form.state || ''} onChange={(e) => setField('state', e.target.value)} className="h-9 text-sm mt-1" data-testid="prop-edit-state" />
              </div>
              <div>
                <Label className="text-xs font-medium">Zip Code</Label>
                <Input value={form.zip || ''} onChange={(e) => setField('zip', e.target.value)} className="h-9 text-sm mt-1" data-testid="prop-edit-zip" />
              </div>
              <div>
                <Label className="text-xs font-medium">EIN (Tax ID)</Label>
                <Input value={form.ein || ''} onChange={(e) => setField('ein', e.target.value)} className="h-9 text-sm mt-1 font-mono" placeholder="XX-XXXXXXX" data-testid="prop-edit-ein" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-medium">MySQL Site ID (Linked Property)</Label>
                <Input value={form.mysql_site_id || ''} onChange={(e) => setField('mysql_site_id', e.target.value)} className="h-9 text-sm mt-1 font-mono" data-testid="prop-edit-site-id" />
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="prop-edit-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Property
              </Button>
              <Button variant="outline" onClick={() => navigate(`/ubm/bill2rollin/property/${propertyId}`)}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
